import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'default' | 'text' | 'ghost' | 'danger' | 'soft-danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  /** Accepts the ui sizes (sm/md/lg) and antd sizes (small/middle/large). */
  size?: ButtonSize | 'small' | 'middle' | 'large';
  icon?: ReactNode;
  loading?: boolean;
  block?: boolean;
  /** When set, renders an anchor styled as a button (antd Button href parity). */
  href?: string;
  target?: string;
  rel?: string;
  /** antd `type` alias → variant. Ignored if `variant` is given. */
  danger?: boolean;
  /** antd Button shape. `circle` → round icon button; `round` → pill. */
  shape?: 'circle' | 'round' | 'default';
  /** Native button type (antd `htmlType`). */
  type?: 'button' | 'submit' | 'reset';
}

const SIZE_ALIAS: Record<string, ButtonSize> = { small: 'sm', middle: 'md', large: 'lg', sm: 'sm', md: 'md', lg: 'lg' };

const VARIANT: Record<ButtonVariant, string> = {
  // Solid fills force white text (`!`) so a container/reset can never bleed dark body-text through.
  primary: 'bg-brand-600 text-white! shadow-soft hover:bg-brand-700 active:bg-brand-800',
  default:
    'bg-surface text-content border border-line hover:border-brand-400 hover:text-brand-700 dark:hover:border-brand-500',
  text: 'bg-transparent text-content hover:bg-surface-2',
  // Soft tinted "secondary action" buttons — light fill, coloured label. Reads far cleaner than a row
  // full of solid green/red blocks, while still carrying an intent colour + text label.
  ghost:
    'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/25 dark:text-brand-300 dark:hover:bg-brand-900/40',
  danger: 'bg-danger-500 text-white! shadow-soft hover:bg-danger-600',
  'soft-danger':
    'bg-danger-500/10 text-danger-600 hover:bg-danger-500/18 dark:bg-danger-500/15 dark:text-danger-400 dark:hover:bg-danger-500/25',
};

// Heights match the form-control scale (Input/Select): sm 32 / md 36 / lg 44, so a button lines up
// with the input or select beside it in a toolbar/filter row instead of towering 4px over it.
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-[13px]',
  md: 'h-9 gap-2 px-4 text-sm',
  lg: 'h-11 gap-2 px-5 text-[15px]',
};

/** The primary action control of the Green Bay UI kit (replaces antd Button). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  // Default size is 'sm' (32px) — the app-wide compact standard. Buttons that must line up with a
  // full-height input/select box opt into size="md" (36px); hero CTAs into size="lg".
  { variant, size = 'sm', icon, loading, block, className, children, disabled, type, href, target, rel, danger, shape, ...rest },
  ref,
) {
  const v: ButtonVariant = variant ?? (danger ? 'danger' : 'default');
  const sz = SIZE_ALIAS[size] ?? 'md';
  const iconOnly = shape === 'circle' || (icon != null && (children == null || children === ''));
  const cls = cn(
    'inline-flex items-center justify-center font-semibold leading-none',
    shape === 'circle' ? 'rounded-full' : shape === 'round' ? 'rounded-pill' : 'rounded-control',
    'transition-colors duration-150 select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
    'disabled:cursor-not-allowed disabled:bg-surface-2! disabled:text-muted! disabled:shadow-none',
    VARIANT[v],
    // Icon-only buttons (incl. shape="circle") render as compact squares — no text padding,
    // so row-action clusters stay tight instead of wide rectangles.
    iconOnly
      ? (sz === 'sm' ? 'size-8 px-0' : sz === 'lg' ? 'size-11 px-0' : 'size-9 px-0')
      : SIZE[sz],
    block && 'w-full',
    className,
  );
  const inner = (
    <>
      {loading ? (
        <Spinner className="-ml-0.5" />
      ) : icon ? (
        <span className="grid place-items-center text-[1.15em] leading-none">{icon}</span>
      ) : null}
      {/* leading-normal (not the button's leading-none) so the truncate/overflow-hidden box is tall
          enough to contain glyph descenders (g, p, y) — otherwise they clip at the bottom. */}
      {children != null && children !== '' && <span className="truncate leading-normal">{children}</span>}
    </>
  );
  // antd Button with `href` renders an anchor styled as a button.
  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={cn(cls, disabled && 'pointer-events-none opacity-60')} {...(rest as Record<string, unknown>)}>
        {inner}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cls}
      {...rest}
    >
      {inner}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Square size in px (the control is a circle of this diameter). */
  diameter?: number;
}

/** Circular icon-only button (theme toggle, notifications, row actions). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { diameter = 40, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'inline-grid place-items-center rounded-pill text-[18px] transition-colors duration-150 [&>svg]:size-[18px]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      style={{ width: diameter, height: diameter }}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Brand spinner used by Button.loading and anywhere a small inline loader is needed. */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-[1.05em] w-[1.05em] animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
